import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BadgeCheck, Building2, Headphones, Layers3, Phone, Radio, Users, Workflow } from "lucide-react";

function Metric({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] px-3 py-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
        <Icon size={13} className="text-[color:var(--accent-blue)]" />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-mono">{value}</div>
      {hint ? <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">{hint}</div> : null}
    </div>
  );
}

function statusTone(status) {
  const normalized = String(status || "active").toLowerCase();
  if (normalized === "active") return "tag-online";
  if (normalized === "warning" || normalized === "expiring") return "tag-warn";
  if (normalized === "suspended" || normalized === "inactive" || normalized === "expired") return "tag-danger";
  return "tag-offline";
}

export default function Licenses() {
  const { user, activeTenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tenants", { params: { _ts: Date.now() } });
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedRows = useMemo(() => {
    const scopedRows = [...rows]
      .filter((row) => {
        if (user?.role === "superadmin") {
          return !activeTenantId || row?.id === activeTenantId;
        }
        return !user?.tenant_id || row?.id === user.tenant_id;
      })
      .sort((a, b) => String(a?.nome || "").localeCompare(String(b?.nome || ""), "pt-BR"));
    return scopedRows;
  }, [rows, user?.role, user?.tenant_id, activeTenantId]);

  return (
    <div className="space-y-5" data-testid="licenses-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Clientes</div>
        <h1 className="text-2xl font-semibold tracking-tight">Licencas por cliente</h1>
        <div className="mt-1 text-sm text-[color:var(--text-secondary)]">
          Visao consolidada dos limites contratados e recursos habilitados de cada empresa.
        </div>
      </div>

      {loading ? (
        <div className="sbc-card px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">Carregando licencas...</div>
      ) : sortedRows.length === 0 ? (
        <div className="sbc-card px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">Nenhum cliente cadastrado.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {sortedRows.map((row) => (
            <section key={row.id} className="sbc-card p-5">
              <div className="flex flex-col gap-3 border-b border-[color:var(--border-default)] pb-4 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-[color:var(--accent-blue)]" />
                    <h2 className="truncate text-lg font-semibold">{row.nome}</h2>
                  </div>
                  <div className="mt-1 text-xs text-[color:var(--text-secondary)]">{row.descricao || "Sem descricao cadastrada"}</div>
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-mono text-[color:var(--text-muted)]">
                    <span>{row.codigo_cliente || "sem codigo"}</span>
                    <span>{row.primary_admin_email || "sem admin principal"}</span>
                    <span>{(row.domains || []).join(", ") || "sem dominio"}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`sbc-tag ${row.enabled ? "tag-online" : "tag-offline"}`}>{row.enabled ? "ATIVO" : "INATIVO"}</span>
                  <span className={`sbc-tag ${statusTone(row.license_status)}`}>{String(row.license_status || "active").toUpperCase()}</span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                <Metric icon={Phone} label="Ramais" value={row.max_extensions ?? 0} hint="Limite total" />
                <Metric icon={Radio} label="Troncos" value={row.max_trunks ?? 0} hint="SIP Trunk / operadoras" />
                <Metric icon={Workflow} label="Canais SIP" value={row.max_channels ?? 0} hint={`Ent ${row.max_channels_inbound ?? 0} | Sai ${row.max_channels_outbound ?? 0}`} />
                <Metric icon={Headphones} label="Agentes" value={row.max_agents ?? 0} hint="Call center" />
                <Metric icon={Users} label="Supervisores" value={row.max_supervisors ?? 0} hint="Supervisao" />
                <Metric icon={Layers3} label="Filas" value={row.max_queues ?? row.max_callcenter ?? 0} hint="Atendimento" />
                <Metric icon={Workflow} label="Recursos fila" value={row.max_queue_resources ?? 0} hint="Membros/recursos" />
                <Metric icon={BadgeCheck} label="IVRs" value={row.max_ivrs ?? 0} hint="URA / menus" />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-sm border border-[color:var(--border-default)] px-3 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Recursos extras</div>
                  <div className="mt-2 space-y-1 text-sm text-[color:var(--text-secondary)]">
                    <div>Conferencias: <span className="font-mono">{row.max_conferences ?? 0}</span></div>
                    <div>Estacionamentos: <span className="font-mono">{row.max_parkings ?? 0}</span></div>
                    <div>Connect devices: <span className="font-mono">{row.max_connect_devices ?? 0}</span></div>
                    <div>Gravacoes: <span className="font-mono">{row.recording_enabled ? "habilitadas" : "desabilitadas"}</span></div>
                  </div>
                </div>
                <div className="rounded-sm border border-[color:var(--border-default)] px-3 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Licenciamento</div>
                  <div className="mt-2 space-y-1 text-sm text-[color:var(--text-secondary)]">
                    <div>Status: <span className="font-mono">{row.license_status || "active"}</span></div>
                    <div>Expira em: <span className="font-mono">{row.license_expires_at || "sem data"}</span></div>
                    <div>Monitor publico: <span className="font-mono">{row.public_monitor_enabled ? "ativo" : "inativo"}</span></div>
                    <div>Retencao gravacoes: <span className="font-mono">{row.recording_retention_days ?? 0} dias</span></div>
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
